import { ChevronDown, ChevronRight, Info, MapPin } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import ScanningImg from '@/assets/images/scanning-Item.png'
import { useGoBack } from '@/hooks/use-go-back'
import { formatDisplayDate } from '@/lib/utils'
import { emitMilestone } from '@/player/sim-progress'
import { RadioOptionRow } from '@/screens/ordering/components/shared/radio-option-row'
import { AppHeader } from '@/ui/app-header'
import { BottomSheet } from '@/ui/bottom-sheet'
import { Button } from '@/ui/button'
import { RadioGroup } from '@/ui/radio-group'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/tabs'
import { ConfirmDialog } from '../shared/confirm-dialog'
import { LocationChip } from './components/location-chip'
import { ProgressHeaderActions } from './components/progress-header-actions'
import { StocktakeLocationSheet } from './components/StocktakeLocationSheet'
import { StocktakeFilterSheet } from './components/stocktake-filter-sheet'
import {
  activeFilterCount,
  EMPTY_STOCKTAKE_FILTER,
  filterFromSearchParams,
  hasActiveFilter,
  matchesStocktakeFilter,
  parseMoney,
  parseQty,
  type StocktakeFilter,
} from './filters'
import { useStocktakeStore } from './store'

interface LocationRow {
  location: string
  qty: string
  exceptions?: number
  value: string
}

type FilterValue = 'location' | 'class'
type SetupTab = 'setup' | 'progress'

const LOCATION_ROWS: LocationRow[] = [
  { location: '1010', qty: '45', value: '$234.60' },
  { location: '1020', qty: '120', value: '$456.80' },
  { location: '1030', qty: '80', value: '$312.50' },
  { location: '1040', qty: '200', value: '$780.20' },
  // Two, to agree with the location sheet a tap away: `location-skus.ts` carries
  // two rows with an `exception`, and `StocktakeLocationSheet` counts them into
  // its "2 SKUs have exceptions" banner.
  { location: '1050', qty: '1,240', exceptions: 2, value: '$3,458.71' },
  { location: '1060', qty: '860', value: '$2,184.30' },
]

/** Authored aggregate for the whole store — see the Totals note below. */
const AUTHORED_TOTAL_QTY = '22,620'
const AUTHORED_TOTAL_VALUE = '$150,541.83'

function ExceptionBadge({ count }: Readonly<{ count: number }>) {
  return (
    <span className="rounded-full border border-orange-500 bg-orange-40 px-3 py-1 text-xs font-medium text-foreground">
      {count} {count === 1 ? 'exception' : 'exceptions'}
    </span>
  )
}

// Totals read as "-" until the count is started, then show the authored store-wide
// aggregate — unless a filter narrows the table, when the on-screen rows are summed.
function totalQtyLabel(started: boolean, filtering: boolean, rowsQty: number): string {
  if (!started) return '-'
  return filtering ? rowsQty.toLocaleString('en-US') : AUTHORED_TOTAL_QTY
}

function totalValueLabel(started: boolean, filtering: boolean, rowsValue: number): string {
  if (!started) return '-'
  if (!filtering) return AUTHORED_TOTAL_VALUE
  return `$${rowsValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
}

/**
 * Mirrors a boolean URL-param flag onto a sheet/dialog's open state in BOTH
 * directions, but only reclaims control once the param itself opened the surface:
 * the learner can still close it by hand, and removing the param later closes it
 * again. The per-caller ref records "opened by the param", so an unrelated render
 * never forces it shut.
 */
function useParamToggle(active: boolean, setOpen: (open: boolean) => void): void {
  const fromParam = useRef(false)
  useEffect(() => {
    if (active) {
      fromParam.current = true
      setOpen(true)
    } else if (fromParam.current) {
      setOpen(false)
      fromParam.current = false
    }
  }, [active, setOpen])
}

function NoStocktakeProgress() {
  return (
    <div className="flex flex-1 items-center justify-center">
      <div
        className="flex flex-col items-center justify-center gap-2 px-4"
        data-tour="stocktake-setup-no-stocktake"
      >
        <img src={ScanningImg} className="w-32 h-32" alt="" />
        <div className="mt-2 flex flex-col items-center gap-2">
          <p className="text-xl font-bold text-foreground text-center">Stocktake has not started</p>
          <p className="text-base text-muted-foreground text-center max-w-77">
            You'll be notified once a date is set
          </p>
        </div>
      </div>
    </div>
  )
}

function StocktakeSetupScreen() {
  const navigate = useNavigate()
  const goBack = useGoBack()
  const [searchParams, setSearchParams] = useSearchParams()
  const initialTab = (searchParams.get('tab') as SetupTab | null) ?? 'setup'
  const allScanned = searchParams.get('allScanned') === '1'
  const approveOpen = searchParams.get('approveOpen') === '1'
  const successOpen = searchParams.get('successOpen') === '1'
  // `?notScannedOpen=1` — lets a lesson land directly on the unscanned-locations
  // sheet. Without it the sheet is only reachable by tapping 'See all', so a step
  // targeting it spotlights nothing.
  const notScannedOpen = searchParams.get('notScannedOpen') === '1'
  // Same trick for the filter sheet, so a lesson can spotlight it without first
  // having to drive the app bar.
  const filterOpenParam = searchParams.get('filterOpen') === '1'
  // `?locationOpen=CODE` — opens the per-location sheet for CODE, so a lesson
  // step can spotlight the exception banner or a flagged SKU without first
  // having to tap a row. The sheet replaces the old `/stocktake/location/:code`
  // route; this param is its deep link.
  const locationOpenParam = searchParams.get('locationOpen')
  const startedParam = searchParams.get('started') === '1'
  const noStocktake = searchParams.get('noStocktake') === '1'
  const tourActive = searchParams.get('tour') !== null
  const maxLocations = useStocktakeStore((s) => s.maxLocations)
  const started = useStocktakeStore((s) => s.started)
  const setStarted = useStocktakeStore((s) => s.setStarted)

  const [tab, setTab] = useState<SetupTab>(initialTab)
  const [sheetOpen, setSheetOpen] = useState<boolean>(false)
  const [sheetLocationOpen, setSheetLocationOpen] = useState<boolean>(notScannedOpen)
  const [selectedFilter, setSelectedFilter] = useState<FilterValue>('location')
  const [approveDialogOpen, setApproveDialogOpen] = useState<boolean>(false)
  const [successDialogOpen, setSuccessDialogOpen] = useState<boolean>(false)
  // Applied filter. The sheet owns its own draft, so nothing lands here until
  // the learner taps Apply.
  const [filter, setFilter] = useState<StocktakeFilter>(EMPTY_STOCKTAKE_FILTER)
  const [filterSheetOpen, setFilterSheetOpen] = useState<boolean>(filterOpenParam)
  const [locationSheetCode, setLocationSheetCode] = useState<string | null>(locationOpenParam)
  const successFromParam = useRef(false)

  useEffect(() => {
    setTab(initialTab)
  }, [initialTab])

  /**
   * URL params own `started` in both directions.
   *
   * `started` lives in the store so it survives the trip to the scanner, but
   * the store outlives a lesson. Without the reset, a finished run leaks into
   * the next visit — the Progress tab shows counted rows and "Start stocktake"
   * is disabled before the learner has done anything. Resetting on every mount
   * (guided and unguided alike) is safe: the guided player re-navigates to its
   * own step URL on every step, so the URL is always the truth; the unguided
   * flow expects a fresh progress tab each time the learner returns here.
   */
  useEffect(() => {
    if (startedParam || allScanned) setStarted(true)
    else setStarted(false)
  }, [startedParam, allScanned, setStarted])

  useEffect(() => {
    if (allScanned) setSheetLocationOpen(false)
  }, [allScanned])

  // While a tour is active, URL params own the filter state so that lesson
  // back-navigation (which rewrites the URL) restores the correct filter and
  // makes conditionally-rendered targets (filter-summary, filter-clear-all)
  // visible. Outside a tour the filter is driven by the Apply button only.
  useEffect(() => {
    if (tourActive) setFilter(filterFromSearchParams(searchParams))
  }, [tourActive, searchParams])

  // Each sheet/dialog below is deep-linkable: its param opens it and, once removed,
  // closes it again — see useParamToggle. successOpen stays inline because it also
  // has to close the approve dialog on the way in.
  useParamToggle(notScannedOpen, setSheetLocationOpen)
  useParamToggle(filterOpenParam, setFilterSheetOpen)
  useParamToggle(approveOpen, setApproveDialogOpen)

  // Same contract as useParamToggle but carries a code: `?locationOpen=1050`
  // opens the sheet for 1050, dropping the param closes it. Emits the milestone
  // on every open so lesson tasks checking it fire regardless of whether the
  // learner tapped a row or deep-linked.
  const locationFromParam = useRef(false)
  useEffect(() => {
    if (locationOpenParam) {
      locationFromParam.current = true
      setLocationSheetCode(locationOpenParam)
      emitMilestone('stocktake-location-viewed')
    } else if (locationFromParam.current) {
      setLocationSheetCode(null)
      locationFromParam.current = false
    }
  }, [locationOpenParam])

  useEffect(() => {
    if (successOpen) {
      successFromParam.current = true
      setApproveDialogOpen(false)
      setSuccessDialogOpen(true)
    } else if (successFromParam.current) {
      setSuccessDialogOpen(false)
      successFromParam.current = false
    }
  }, [successOpen])

  const filterCount = activeFilterCount(filter)
  const filtering = hasActiveFilter(filter)

  const visibleRows = useMemo(
    () =>
      LOCATION_ROWS.filter((row) =>
        matchesStocktakeFilter(filter, { qty: parseQty(row.qty), value: parseMoney(row.value) }),
      ),
    [filter],
  )

  // Totals. Unfiltered, the authored store-wide aggregate stands — it covers all
  // of the stocktake, not just the rows this table lists. Once a filter narrows
  // the table the aggregate would be a lie, so it is recomputed from what is on
  // screen, which is what the design's filtered frame shows.
  const rowsQty = visibleRows.reduce((sum, r) => sum + parseQty(r.qty), 0)
  const rowsValue = visibleRows.reduce((sum, r) => sum + parseMoney(r.value), 0)

  function handleTabChange(v: string) {
    const next = v as SetupTab
    setTab(next)
    setSearchParams(
      (prev) => {
        prev.set('tab', next)
        return prev
      },
      { replace: true },
    )
    if (next === 'progress') emitMilestone('stocktake-progress-viewed')
  }

  function clearFilter() {
    setFilter(EMPTY_STOCKTAKE_FILTER)
    setFilterSheetOpen(false)
  }

  function openLocation(code: string) {
    setLocationSheetCode(code)
    emitMilestone('stocktake-location-viewed')
  }

  return (
    <div className="flex h-full flex-col">
      <AppHeader
        title="Stocktake"
        onBack={() => goBack()}
        actions={
          tab === 'progress' ? (
            <ProgressHeaderActions
              filterActive={filtering}
              onFilter={() => setFilterSheetOpen(true)}
              onSearch={() => navigate('/stocktake/setup/sku-search')}
            />
          ) : undefined
        }
      />

      {/* Store location banner */}
      <div className="bg-accent px-4 pb-16 pt-4">
        <div className="flex items-center gap-2">
          <span className="flex shrink-0 items-center justify-center rounded-full bg-primary p-2">
            <MapPin className="size-4 text-white" />
          </span>
          <p className="text-xl font-bold text-foreground">AMK Finest (301)</p>
        </div>
      </div>

      {/* White card — overlaps the blue banner */}
      <div className="-mt-8 flex flex-1 flex-col overflow-hidden rounded-t-4xl bg-card">
        <Tabs
          value={tab}
          onValueChange={handleTabChange}
          className="flex min-h-0 flex-1 flex-col gap-0"
        >
          <TabsList variant="line" className="h-auto w-full rounded-none p-0 px-4">
            <TabsTrigger
              value="setup"
              data-tour="stocktake-setup-tab"
              className="flex-1 py-4 font-medium data-[state=active]:font-bold after:bg-primary"
            >
              Setup
            </TabsTrigger>
            <TabsTrigger
              value="progress"
              data-tour="stocktake-progress-tab"
              className="flex-1 py-4 font-medium data-[state=active]:font-bold after:bg-primary"
            >
              Progress
            </TabsTrigger>
          </TabsList>

          <TabsContent value="setup" className="flex-1 overflow-y-auto p-4">
            <div
              data-tour="stocktake-setup-card"
              className="rounded-xl border border-border bg-card p-4"
            >
              <p className="mb-3 text-base font-bold text-foreground">Next stocktake</p>

              <div className="mb-3 flex flex-col gap-0.5">
                <p className="text-sm text-muted-foreground">Stocktake date</p>
                <p className="text-sm font-medium text-foreground">
                  {formatDisplayDate(new Date(Date.now()))}
                </p>
              </div>

              <div className="mb-3 flex flex-col gap-0.5">
                <p className="text-sm text-muted-foreground">No. of locations</p>
                <p className="text-sm font-medium text-foreground">
                  {maxLocations} (remembered from last stocktake)
                </p>
              </div>

              <button
                type="button"
                data-tour="stocktake-setup-edit-link"
                className="text-base font-medium text-primary underline"
                onClick={() => navigate('/stocktake/setup/edit')}
              >
                Edit location
              </button>
            </div>
          </TabsContent>

          <TabsContent value="progress" className="flex flex-1 flex-col overflow-hidden">
            {noStocktake && <NoStocktakeProgress />}
            {!noStocktake && (
              <>
                {filtering && (
                  <div
                    data-tour="stocktake-filter-summary"
                    className="flex items-center justify-between px-4 pb-2.5 pt-1.5 text-xs font-medium"
                  >
                    <p className="text-foreground-subtle">
                      {visibleRows.length} of {LOCATION_ROWS.length} locations &middot;{' '}
                      {filterCount} {filterCount === 1 ? 'filter' : 'filters'}
                    </p>
                    <button
                      type="button"
                      data-tour="stocktake-filter-clear-all"
                      className="text-primary"
                      onClick={clearFilter}
                    >
                      Clear all
                    </button>
                  </div>
                )}

                {/* Table column headers */}
                <div className="flex items-center justify-between px-4 pb-2 pt-4">
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      className="flex items-center gap-0.5"
                      onClick={() => setSheetOpen(true)}
                    >
                      {selectedFilter === 'location' ? (
                        <span className="text-sm text-foreground">Loc</span>
                      ) : (
                        <span className="text-sm text-foreground">Cls</span>
                      )}
                      <ChevronDown className="size-4 text-foreground" />
                    </button>
                    <span className="text-sm text-foreground">QTY/Exception</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-foreground">Value</span>
                  </div>
                </div>
                <div className="h-px w-full bg-border" />

                {!allScanned && started && (
                  <div className="mx-4 mt-3 flex justify-between rounded-xl bg-accent p-3">
                    <div className="flex flex-1">
                      <div
                        className="flex items-start gap-2"
                        data-tour="stocktake-not-scanned-banner"
                      >
                        <Info className="mt-0.5 size-5 shrink-0 text-[#1a3a7c]" />
                        <p className="text-base leading-6 font-bold text-black">
                          87 locations not scanned
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      data-tour="stocktake-not-scanned-see-all"
                      className="text-black underline text-base font-bold"
                      onClick={() => {
                        emitMilestone('stocktake-not-scanned-viewed')
                        setSheetLocationOpen(true)
                      }}
                    >
                      See all
                    </button>
                  </div>
                )}

                {/* Scrollable location list */}
                <div className="flex-1 overflow-y-auto">
                  <div data-tour="stocktake-progress-content">
                    {(started ? visibleRows : []).map((row, i) => {
                      // Both anchors sit on the FIRST row that qualifies, so a
                      // coach mark always resolves to exactly one node. They can
                      // land on the same row once a filter hides 1010-1040, so
                      // the two never share an element: the button takes the
                      // more specific one and the wrapper takes the other.
                      const isFirstException =
                        row.exceptions !== undefined &&
                        visibleRows.findIndex((r) => r.exceptions !== undefined) === i
                      const firstRowTour = i === 0 ? 'stocktake-progress-first-row' : undefined
                      const rowTour = isFirstException
                        ? 'stocktake-progress-exception-row'
                        : firstRowTour
                      const wrapperTour = isFirstException ? firstRowTour : undefined
                      return (
                        <div key={row.location} data-tour={wrapperTour}>
                          <button
                            type="button"
                            data-tour={rowTour}
                            className="flex w-full items-center justify-between px-4 py-2 text-left"
                            onClick={() => openLocation(row.location)}
                          >
                            <div className="flex items-center gap-4">
                              <LocationChip code={row.location} />
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-base font-bold text-foreground">
                                  {row.qty}
                                </span>
                                {row.exceptions !== undefined && (
                                  <ExceptionBadge count={row.exceptions} />
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-base font-bold text-foreground">
                                {row.value}
                              </span>
                              <ChevronRight className="size-4 text-muted-foreground" />
                            </div>
                          </button>
                          {i < visibleRows.length - 1 && <div className="h-px w-full bg-border" />}
                        </div>
                      )
                    })}
                  </div>

                  {/* Summary row */}
                  <div className="h-px w-full bg-border" />
                  <div className="flex items-center justify-between px-4 py-2 text-xs font-medium text-foreground">
                    <span>Total: {started ? visibleRows.length : '-'}</span>
                    <span>QTY: {totalQtyLabel(started, filtering, rowsQty)}</span>
                    <span>Value: {totalValueLabel(started, filtering, rowsValue)}</span>
                  </div>
                </div>

                {/* Footer actions */}
                <div className="bg-card px-4 pb-[max(2.5rem,env(safe-area-inset-bottom,0px))] pt-4 shadow-[0px_-4px_5px_rgba(0,0,0,0.1)]">
                  <div className="flex flex-col gap-4">
                    {filtering && (
                      <p className="text-center text-xs font-medium text-foreground-subtle">
                        Clear filters to approve all {LOCATION_ROWS.length} locations
                      </p>
                    )}
                    <Button
                      data-tour="stocktake-approve-btn"
                      disabled={!started || filtering}
                      className="h-12 w-full rounded-full"
                      onClick={() => setApproveDialogOpen(true)}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      data-tour="stocktake-progress-start-btn"
                      disabled={started}
                      className="h-12 w-full rounded-full border-foreground"
                      onClick={() => {
                        setStarted(true)
                        emitMilestone('stocktake-started')
                        void navigate('/stocktake/scan-location')
                      }}
                    >
                      Start stocktake
                    </Button>
                  </div>
                </div>
              </>
            )}

            <ConfirmDialog
              open={approveDialogOpen}
              title="Approve stocktake for AMK Finest (301)?"
              description={
                <p className="text-black text-base">
                  Once approved, the count is final and cannot be edited in OneApp.
                </p>
              }
              primaryLabel="Approve"
              primaryDataTour="stocktake-approve-dialog-btn"
              onPrimary={() => {
                setApproveDialogOpen(false)
                emitMilestone('stocktake-approved')
                setSuccessDialogOpen(true)
              }}
              secondaryLabel="Cancel"
              onSecondary={() => setApproveDialogOpen(false)}
            />

            <ConfirmDialog
              iconVariant="success"
              open={successDialogOpen}
              title="Stocktake approved successfully"
              description={<p className="text-black text-base">Your stocktake has been approved</p>}
              primaryLabel="Okay"
              primaryDataTour="stocktake-success-dialog-btn"
              onPrimary={() => setSuccessDialogOpen(false)}
            />

            <StocktakeFilterSheet
              open={filterSheetOpen}
              filter={filter}
              onClose={() => setFilterSheetOpen(false)}
              onApply={(next) => {
                setFilter(next)
                setFilterSheetOpen(false)
                emitMilestone('stocktake-filter-applied')
              }}
            />

            <StocktakeLocationSheet
              open={locationSheetCode !== null}
              locationCode={locationSheetCode}
              filter={filter}
              onClose={() => setLocationSheetCode(null)}
            />

            <BottomSheet open={sheetOpen} onClose={() => setSheetOpen(false)} title="Select">
              <RadioGroup
                value={selectedFilter}
                onValueChange={(v) => {
                  setSelectedFilter(v as FilterValue)
                  setSheetOpen(false)
                }}
                className="flex flex-col gap-3"
              >
                <RadioOptionRow
                  id="filter-location"
                  value="location"
                  label="Location"
                  selected={selectedFilter === 'location'}
                />
                <RadioOptionRow
                  id="filter-class"
                  value="class"
                  label="Class"
                  selected={selectedFilter === 'class'}
                />
              </RadioGroup>
            </BottomSheet>

            <BottomSheet
              dataTour="stocktake-not-scanned-sheet"
              open={sheetLocationOpen}
              onClose={() => setSheetLocationOpen(false)}
              title="Locations not scanned"
              // l4-12 coachmarks this sheet, and the tour overlay (z-100) always paints
              // over it (z-90). At the shared 85dvh the sheet leaves too little room
              // above for the card, so the card landed on this sheet's own title and
              // Close button. The location list already scrolls inside the sheet.
              className="max-h-[50dvh]"
            >
              <div className="space-y-4">
                <div className="flex flex-col rounded-xl bg-accent p-3">
                  <div className="flex flex-1 items-start gap-2">
                    <Info className="mt-0.5 size-5 shrink-0 text-[#1a3a7c]" />
                    <p className="text-base leading-6 font-bold text-black">
                      87 of 120 locations not scanned
                    </p>
                  </div>
                  <p className="tex-sm text-black">
                    You can still approve, but these locations will have no count.
                  </p>
                </div>
                <div className="flex flex-col rounded-xl border border-border bg-white">
                  {['1071', '1121', '1181', '1131', '1141'].map((code, i) => (
                    <div key={code}>
                      {i > 0 && <div className="h-px w-full bg-divider-subtle" />}
                      <div className="flex gap-3 p-3 items-center">
                        <div className="flex items-center rounded-3xl font-bold bg-accent px-1 h-8 text-black text-sm">
                          {code}
                        </div>
                        <p className="flex-1 text-[#111]">Not Scanned</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </BottomSheet>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}

export default StocktakeSetupScreen
